import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Calendar, DollarSign, MapPin, Clock, Building, Users, Award, RefreshCw, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

function HRSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const [configData, setConfigData] = useState({
    leaveTypes: [],
    payroll: null,
    attendance: null,
    company: null,
    holidays: { total: 0, mandatory: 0, restricted: 0 },
    departments: 0,
    locations: 0,
    grades: 0,
    profiles: 0
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  useEffect(() => {
    fetchAllConfigData();
  }, []);

  const fetchAllConfigData = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all config data in parallel
      const [
        leaveTypesRes,
        payrollRes,
        attendanceRes,
        companyRes,
        holidaysRes,
        departmentsRes,
        locationsRes,
        gradesRes,
        profilesRes
      ] = await Promise.allSettled([
        axios.get(`${API}/api/hr-config/leave-types`, { headers }),
        axios.get(`${API}/api/hr-config/payroll`, { headers }),
        axios.get(`${API}/api/hr-config/attendance-rules`, { headers }),
        axios.get(`${API}/api/hr-config/company-info`, { headers }),
        axios.get(`${API}/api/holidays/2026`, { headers }),
        axios.get(`${API}/api/org-structure/departments`, { headers }),
        axios.get(`${API}/api/master-data/locations`, { headers }),
        axios.get(`${API}/api/org-structure/grades`, { headers }),
        axios.get(`${API}/api/org-structure/profiles`, { headers })
      ]);

      setConfigData({
        leaveTypes: leaveTypesRes.status === 'fulfilled' ? leaveTypesRes.value.data.leave_types || [] : [],
        payroll: payrollRes.status === 'fulfilled' ? payrollRes.value.data : null,
        attendance: attendanceRes.status === 'fulfilled' ? attendanceRes.value.data : null,
        company: companyRes.status === 'fulfilled' ? companyRes.value.data : null,
        holidays: holidaysRes.status === 'fulfilled' ? {
          total: holidaysRes.value.data.total || 0,
          mandatory: holidaysRes.value.data.mandatory?.length || 0,
          restricted: holidaysRes.value.data.restricted?.length || 0
        } : { total: 0, mandatory: 0, restricted: 0 },
        departments: departmentsRes.status === 'fulfilled' ? (departmentsRes.value.data.departments?.length || (Array.isArray(departmentsRes.value.data) ? departmentsRes.value.data.length : 0)) : 0,
        locations: locationsRes.status === 'fulfilled' ? (locationsRes.value.data.locations?.length || 0) : 0,
        grades: gradesRes.status === 'fulfilled' ? (gradesRes.value.data.grades?.length || 0) : 0,
        profiles: profilesRes.status === 'fulfilled' ? (profilesRes.value.data.profiles?.length || 0) : 0
      });
    } catch (error) {
      console.error('Failed to fetch config data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Helper to get leave summary
  const getLeaveSummary = () => {
    if (!configData.leaveTypes || configData.leaveTypes.length === 0) {
      return { total: 0, breakdown: 'Not configured' };
    }
    const total = configData.leaveTypes.reduce((sum, lt) => sum + (lt.days_per_year || 0), 0);
    const breakdown = configData.leaveTypes
      .map(lt => `${lt.leave_type?.replace(' Leave', '')}: ${lt.days_per_year || 0}`)
      .join(', ');
    return { total, breakdown };
  };

  const leaveSummary = getLeaveSummary();

  // Fetch data summary for purge modal
  const fetchDataSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/data-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch data summary:', error);
    }
  };

  // Handle purge button click
  const handlePurgeClick = async () => {
    await fetchDataSummary();
    setShowPurgeModal(true);
    setPurgeResult(null);
  };

  // Execute purge
  const executePurge = async () => {
    setPurging(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/purge-test-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurgeResult(response.data);
      // Refresh config data after purge
      fetchAllConfigData();
    } catch (error) {
      console.error('Purge failed:', error);
      setPurgeResult({ 
        success: false, 
        message: error.response?.data?.detail || 'Purge failed' 
      });
    } finally {
      setPurging(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>HR Settings & Configuration</h1>
            <p className="text-gray-600 mt-2">Manage company-wide HR policies and organizational structure</p>
          </div>
          <button
            onClick={fetchAllConfigData}
            disabled={dataLoading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Configuration Modules */}
        <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Configuration Modules</h2>
        
        {dataLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-500">Loading configuration data...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Leave Configuration */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/leave-config')}>
              <Calendar className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Leave Configuration</h3>
              <p className="text-gray-600 text-sm">Manage leave types and policies</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• Total: {leaveSummary.total} days/year</p>
                <p>• {leaveSummary.breakdown}</p>
              </div>
            </div>

            {/* Holiday Calendar */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/holidays')}>
              <Calendar className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Holiday Calendar</h3>
              <p className="text-gray-600 text-sm">View 2026 holidays</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• {configData.holidays.mandatory} mandatory</p>
                <p>• {configData.holidays.restricted} restricted</p>
              </div>
            </div>

            {/* Payroll Settings */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/payroll-configuration')}>
              <DollarSign className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Payroll Configuration</h3>
              <p className="text-gray-600 text-sm">Salary structure & statutory</p>
              <div className="mt-4 text-sm text-gray-500">
                {configData.payroll ? (
                  <>
                    <p>• {configData.payroll.basic_percentage || 40}% Basic, {configData.payroll.hra_percentage_of_basic || 50}% HRA</p>
                    <p>• PF: {configData.payroll.pf_employee_percentage || 12}%+{configData.payroll.pf_employer_percentage || 12}%</p>
                  </>
                ) : (
                  <p>• Not configured</p>
                )}
              </div>
            </div>

            {/* Branch Management */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/branch-config')}>
              <MapPin className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Branch Management</h3>
              <p className="text-gray-600 text-sm">Locations & geo-fencing</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• {configData.locations} locations</p>
                <p>• Geo-fence enabled</p>
              </div>
            </div>

            {/* Attendance Rules */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/attendance-config')}>
              <Clock className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Attendance Rules</h3>
              <p className="text-gray-600 text-sm">Working hours & grace</p>
              <div className="mt-4 text-sm text-gray-500">
                {configData.attendance ? (
                  <>
                    <p>• Grace: {configData.attendance.grace_period_minutes || 15} min ({configData.attendance.grace_allowed_per_month || 3}x)</p>
                    <p>• Week-off: {configData.attendance.week_off || 'Sunday'}</p>
                  </>
                ) : (
                  <p>• Not configured</p>
                )}
              </div>
            </div>

            {/* Travel Rates */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/travel-rates-config')}>
              <MapPin className="w-12 h-12 mb-4" style={{color: '#ff9800'}} />
              <h3 className="text-xl font-bold mb-2">Travel Rates</h3>
              <p className="text-gray-600 text-sm">Per-km reimbursement rates</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• 2-Wheeler rates</p>
                <p>• 4-Wheeler rates</p>
              </div>
            </div>

            {/* Company Information */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/company-info')}>
              <Building className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Company Info</h3>
              <p className="text-gray-600 text-sm">Legal details & registrations</p>
              <div className="mt-4 text-sm text-gray-500">
                {configData.company ? (
                  <>
                    <p>• {configData.company.legal_name || 'Not set'}</p>
                    <p>• PAN: {configData.company.pan_number || 'Not set'}</p>
                  </>
                ) : (
                  <p>• Not configured</p>
                )}
              </div>
            </div>

            {/* Departments */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/departments-config')}>
              <Building className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Departments</h3>
              <p className="text-gray-600 text-sm">Manage departments</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• {configData.departments} departments</p>
                <p>• Click to manage</p>
              </div>
            </div>

            {/* Profiles */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/profiles-config')}>
              <Users className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Profiles</h3>
              <p className="text-gray-600 text-sm">Job profiles & mappings</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• {configData.profiles || 0} profiles</p>
                <p>• Grade mapped</p>
              </div>
            </div>

            {/* Grades */}
            <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                 onClick={() => navigate('/grades-config')}>
              <Award className="w-12 h-12 mb-4" style={{color: '#2b3380'}} />
              <h3 className="text-xl font-bold mb-2">Grades & Bands</h3>
              <p className="text-gray-600 text-sm">Salary bands structure</p>
              <div className="mt-4 text-sm text-gray-500">
                <p>• {configData.grades} grades</p>
                <p>• Click to view salary bands</p>
              </div>
            </div>

            {/* Purge Data Tool - Admin Only */}
            {user?.role === 'Admin' && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                   onClick={handlePurgeClick}>
                <Trash2 className="w-12 h-12 mb-4 text-red-500" />
                <h3 className="text-xl font-bold mb-2 text-red-700">Purge Test Data</h3>
                <p className="text-red-600 text-sm">Delete all test/demo data</p>
                <div className="mt-4 text-sm text-red-500">
                  <p>• Removes test employees</p>
                  <p>• Keeps your admin account</p>
                  <p>• Preserves config data</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Purge Confirmation Modal */}
        {showPurgeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
              {!purgeResult ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                    <h2 className="text-2xl font-bold text-red-700">Purge Test Data</h2>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    This will permanently delete the following data:
                  </p>
                  
                  {dataSummary ? (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">Employees</td>
                            <td className="py-2 font-bold text-right text-red-600">{dataSummary.employees - 1} to delete</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">User Accounts</td>
                            <td className="py-2 font-bold text-right text-red-600">{dataSummary.users - 1} to delete</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">Candidates</td>
                            <td className="py-2 font-bold text-right text-red-600">{dataSummary.candidates} to delete</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">Job Postings</td>
                            <td className="py-2 font-bold text-right text-red-600">{dataSummary.job_postings} to delete</td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">Attendance Records</td>
                            <td className="py-2 font-bold text-right text-red-600">{dataSummary.attendance_records} to delete</td>
                          </tr>
                          <tr>
                            <td className="py-2 text-gray-600">Leave Requests</td>
                            <td className="py-2 font-bold text-right text-red-600">{dataSummary.leave_requests} to delete</td>
                          </tr>
                        </tbody>
                      </table>
                      
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-green-600 font-medium">✓ Your admin account will be preserved</p>
                        <p className="text-green-600 font-medium">✓ Departments ({dataSummary.departments}) will be kept</p>
                        <p className="text-green-600 font-medium">✓ Designations ({dataSummary.designations}) will be kept</p>
                        <p className="text-green-600 font-medium">✓ Locations ({dataSummary.locations}) will be kept</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center py-4">
                      <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowPurgeModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      disabled={purging}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={executePurge}
                      disabled={purging || !dataSummary}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {purging ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Purging...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Confirm Purge
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    {purgeResult.success ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                    )}
                    <h2 className="text-2xl font-bold">
                      {purgeResult.success ? 'Purge Complete!' : 'Purge Failed'}
                    </h2>
                  </div>
                  
                  {purgeResult.success ? (
                    <div className="bg-green-50 rounded-lg p-4 mb-4">
                      <p className="font-medium text-green-700 mb-3">Deleted:</p>
                      <ul className="text-sm text-green-600 space-y-1">
                        <li>• {purgeResult.deleted?.employees_deleted || 0} employees</li>
                        <li>• {purgeResult.deleted?.users_deleted || 0} user accounts</li>
                        <li>• {purgeResult.deleted?.candidates_deleted || 0} candidates</li>
                        <li>• {purgeResult.deleted?.job_postings_deleted || 0} job postings</li>
                        <li>• {purgeResult.deleted?.attendance_deleted || 0} attendance records</li>
                      </ul>
                      
                      <p className="font-medium text-green-700 mt-4 mb-2">Remaining:</p>
                      <ul className="text-sm text-green-600 space-y-1">
                        <li>• {purgeResult.deleted?.remaining?.employees || 0} employees (your account)</li>
                        <li>• {purgeResult.deleted?.remaining?.departments || 0} departments</li>
                        <li>• {purgeResult.deleted?.remaining?.designations || 0} designations</li>
                        <li>• {purgeResult.deleted?.remaining?.locations || 0} locations</li>
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-red-50 rounded-lg p-4 mb-4">
                      <p className="text-red-600">{purgeResult.message}</p>
                    </div>
                  )}
                  
                  <button
                    onClick={() => {
                      setShowPurgeModal(false);
                      setPurgeResult(null);
                    }}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default HRSettings;
